import { useCallback, useMemo, useRef, useState } from "react";

import { cursorIndex, requestPrompt } from "../model";
import type { KeyFact, KeyFactActionBody, QueueCursor } from "../types";
import type { KeyFactActions } from "./useKeyFactActions";

/** Where the attorney is in the Insights tab: the ask in focus, the row being
 * edited, and what was skipped or requested from the client this visit. */
export type InsightsFlow = {
  /** The facts still asking, in queue order (skipped and requested left out). */
  open: KeyFact[];
  current: KeyFact | null;
  editing: string | null;
  skipped: ReadonlySet<string>;
  requested: ReadonlySet<string>;
  focus: (key: string) => void;
  next: () => void;
  edit: (key: string | null) => void;
  skip: (key: string) => void;
  unskip: (key: string) => void;
  respond: (fact: KeyFact, body: KeyFactActionBody) => Promise<void>;
  request?: (fact: KeyFact) => void;
  unrequest: (key: string) => void;
  upload?: (fact: KeyFact) => void;
  /** The row element `next` scrolls to. */
  register: (key: string) => (el: HTMLElement | null) => void;
};

const without = (set: ReadonlySet<string>, key: string): Set<string> => {
  const out = new Set(set);
  out.delete(key);
  return out;
};

export const useInsightsFlow = (
  queue: readonly KeyFact[],
  actions: KeyFactActions,
  options: {
    onPrompt?: (text: string) => void;
    onUpload?: (fact: KeyFact) => void;
  }
): InsightsFlow => {
  const { onPrompt, onUpload } = options;
  const [cursor, setCursor] = useState<QueueCursor>({ key: null, index: 0 });
  const [editing, setEditing] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set());
  const [requested, setRequested] = useState<ReadonlySet<string>>(new Set());
  const rows = useRef(new Map<string, HTMLElement>());

  const open = useMemo(
    () => queue.filter((f) => !skipped.has(f.key) && !requested.has(f.key)),
    [queue, skipped, requested]
  );
  const index = open.length ? cursorIndex(open, cursor) : -1;
  const current = index === -1 ? null : open[index];

  /** The fact at `key` leaves the open asks: the one after it takes focus. */
  const leave = useCallback(
    (key: string) => {
      const at = open.findIndex((f) => f.key === key);
      if (at !== -1 && current?.key === key)
        setCursor({ key: null, index: at });
    },
    [open, current]
  );

  const focus = useCallback(
    (key: string) => {
      const at = open.findIndex((f) => f.key === key);
      if (at !== -1) setCursor({ key, index: at });
    },
    [open]
  );

  const next = useCallback(() => {
    if (!open.length) return;
    const at = index === -1 ? 0 : (index + 1) % open.length;
    const fact = open[at];
    setCursor({ key: fact.key, index: at });
    rows.current
      .get(fact.key)
      ?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [open, index]);

  const respond = useCallback(
    async (fact: KeyFact, body: KeyFactActionBody) => {
      if (!(await actions.act(fact.key, body))) return;
      setEditing((key) => (key === fact.key ? null : key));
      setRequested((set) => without(set, fact.key));
      leave(fact.key);
    },
    [actions, leave]
  );

  const skip = useCallback(
    (key: string) => {
      leave(key);
      setEditing((at) => (at === key ? null : at));
      setSkipped((set) => new Set(set).add(key));
    },
    [leave]
  );

  const unskip = useCallback((key: string) => {
    setSkipped((set) => without(set, key));
    setCursor({ key, index: 0 });
  }, []);

  const request = useCallback(
    (fact: KeyFact) => {
      onPrompt?.(requestPrompt(fact));
      leave(fact.key);
      setRequested((set) => new Set(set).add(fact.key));
    },
    [onPrompt, leave]
  );

  const unrequest = useCallback((key: string) => {
    setRequested((set) => without(set, key));
    setCursor({ key, index: 0 });
  }, []);

  const edit = useCallback(
    (key: string | null) => {
      setEditing(key);
      if (key) focus(key);
    },
    [focus]
  );

  const register = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) rows.current.set(key, el);
      else rows.current.delete(key);
    },
    []
  );

  return {
    open,
    current,
    editing,
    skipped,
    requested,
    focus,
    next,
    edit,
    skip,
    unskip,
    respond,
    request: onPrompt ? request : undefined,
    unrequest,
    upload: onUpload,
    register
  };
};
